import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Drawer, Input, Typography } from '@arco-design/web-react';
import { IconFile } from '@arco-design/web-react/icon';
import { errorMessage } from '../../../common/api/httpClient';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';
import SortableSelectedItems from '../../../components/SortableSelectedItems';
import { mergePageGroups, pageIdentity, pagesApi, type PortalPage,
  type PortalPageGroup, type PortalPageGroupFilter,
  type PortalPageIdentity } from '../api/pagesApi';

const { Text } = Typography;
const PAGE_SIZE = 40;

type Props = {
  favoritePages: PortalPage[];
  onSave: (pages: PortalPageIdentity[]) => Promise<void>;
  onClose: () => void;
};

export default function PageFavoritesConfig(props: Props) {
  const { message } = usePortalOverlay();
  const [groups, setGroups] = useState<PortalPageGroup[]>([]);
  const [filters, setFilters] = useState<PortalPageGroupFilter[]>([]);
  const [knownPages, setKnownPages] = useState<PortalPage[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [applicationCode, setApplicationCode] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [initialError, setInitialError] = useState<string>();
  const [retry, setRetry] = useState(0);
  const queryGeneration = useRef(0);
  const loadingMore = useRef(false);

  useEffect(() => {
    let active = true;
    void pagesApi.catalog({ page: 1, pageSize: 100, favoriteOnly: true })
      .then(catalog => {
        if (!active) return;
        const availablePages = catalog.groups.flatMap(group => group.pages);
        const available = new Set(availablePages.map(pageIdentity));
        setKnownPages(availablePages);
        setSelectedIds(props.favoritePages.map(pageIdentity).filter(id => available.has(id)));
      })
      .catch(requestError => {
        if (active) setInitialError(errorMessage(requestError));
      })
      .finally(() => { if (active) setInitializing(false); });
    return () => { active = false; };
  }, [props.favoritePages]);

  useEffect(() => {
    const generation = ++queryGeneration.current;
    loadingMore.current = false;
    setGroups([]);
    setPage(0);
    setTotal(0);
    setLoading(true);
    setError(undefined);
    const timer = window.setTimeout(() => {
      void pagesApi.catalog({ page: 1, pageSize: PAGE_SIZE,
        applicationCode: applicationCode === 'all' ? undefined : applicationCode,
        keyword })
        .then(catalog => {
          if (generation !== queryGeneration.current) return;
          setGroups(catalog.groups);
          setFilters(catalog.groupFilters);
          setPage(catalog.page);
          setTotal(catalog.total);
        })
        .catch(requestError => {
          if (generation === queryGeneration.current) setError(errorMessage(requestError));
        })
        .finally(() => {
          if (generation === queryGeneration.current) setLoading(false);
        });
    }, keyword.trim() ? 250 : 0);
    return () => {
      window.clearTimeout(timer);
      queryGeneration.current++;
    };
  }, [applicationCode, keyword, retry]);

  const loadMore = () => {
    if (loadingMore.current || loading || page * PAGE_SIZE >= total) return;
    loadingMore.current = true;
    setLoading(true);
    setError(undefined);
    const generation = queryGeneration.current;
    void pagesApi.catalog({ page: page + 1, pageSize: PAGE_SIZE,
      applicationCode: applicationCode === 'all' ? undefined : applicationCode,
      keyword })
      .then(catalog => {
        if (generation !== queryGeneration.current) return;
        setGroups(current => mergePageGroups(current, catalog.groups));
        setPage(catalog.page);
        setTotal(catalog.total);
      })
      .catch(requestError => {
        if (generation === queryGeneration.current) setError(errorMessage(requestError));
      })
      .finally(() => {
        if (generation === queryGeneration.current) {
          loadingMore.current = false;
          setLoading(false);
        }
      });
  };

  const pageMap = new Map([...knownPages, ...groups.flatMap(group => group.pages)]
    .map(item => [pageIdentity(item), item]));
  const visiblePages = groups.flatMap(group => group.pages);

  const togglePage = (item: PortalPage) => {
    const id = pageIdentity(item);
    setKnownPages(current => current.some(page => pageIdentity(page) === id)
      ? current : [...current, item]);
    setSelectedIds(current => current.includes(id)
      ? current.filter(value => value !== id)
      : current.length < 20 ? [...current, id] : current);
  };

  const save = () => {
    const selected = selectedIds.map(id => pageMap.get(id));
    if (selected.some(item => !item)) {
      message.error('所选页面已失效，请重新打开配置');
      return;
    }
    setSaving(true);
    void props.onSave(selected.map(item => ({
      applicationCode: item!.applicationCode, pageCode: item!.pageCode,
    }))).then(() => {
      props.onClose();
      message.success('常用界面已更新');
    }).catch(requestError => message.error(errorMessage(requestError)))
      .finally(() => setSaving(false));
  };

  return <Drawer title="配置常用界面" visible width={720}
    className="portal-favorites-config-drawer" onCancel={props.onClose}
    footer={<div className="portal-common-config__footer">
      <Text type="secondary">已选择 {selectedIds.length} 项</Text>
      <div>
        <Button onClick={props.onClose}>取消</Button>
        <Button type="primary" loading={saving}
          disabled={initializing || Boolean(initialError) || Boolean(error)} onClick={save}>
          保存配置
        </Button>
      </div>
    </div>}>
    <div className="portal-common-config portal-common-config--favorites">
      {initializing ? <Text type="secondary">正在核对已选页面...</Text> : null}
      {initialError ? <Text type="error">{initialError}</Text> : null}
      <Input.Search allowClear value={keyword} placeholder="搜索页面名称"
        onChange={setKeyword} />
      <div className="portal-operation-list-tabs">
        <button className={applicationCode === 'all' ? 'active' : ''} type="button"
          onClick={() => setApplicationCode('all')}>全部</button>
        {filters.map(filter => <button key={filter.applicationCode} type="button"
          className={applicationCode === filter.applicationCode ? 'active' : ''}
          onClick={() => setApplicationCode(filter.applicationCode)}>{filter.applicationName}</button>)}
      </div>
      <div className="portal-common-config__body">
        <div className="portal-common-config__options portal-common-config__options--favorites"
          onScroll={event => {
            const target = event.currentTarget;
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 80) loadMore();
          }}>
          {visiblePages.map(item => <label className="portal-common-config__option"
            key={pageIdentity(item)}>
            <Checkbox checked={selectedIds.includes(pageIdentity(item))}
              disabled={initializing || (selectedIds.length >= 20 && !selectedIds.includes(pageIdentity(item)))}
              onChange={() => togglePage(item)} />
            <span className="portal-operation-card__icon"><IconFile /></span>
            <span><strong>{item.name}</strong><small>{item.applicationName}</small></span>
          </label>)}
          {loading ? <Text type="secondary">正在加载页面...</Text> : null}
          {error ? <Text type="error">{error}</Text> : null}
          {!loading && !error && visiblePages.length === 0
            ? <Text type="secondary">暂无可配置页面</Text> : null}
          {page * PAGE_SIZE < total ? <Button size="small" loading={loading}
            onClick={loadMore}>加载更多</Button> : null}
          {error ? <Button size="small" onClick={() => page === 0
            ? setRetry(value => value + 1) : loadMore()}>重试</Button> : null}
          <Text type="secondary">已加载 {visiblePages.length} / {total} 项</Text>
        </div>
        <div className="portal-common-config__selected portal-common-config__selected--favorites">
          <SortableSelectedItems
            items={selectedIds.flatMap(id => {
              const item = pageMap.get(id);
              return item ? [{ id, label: item.name }] : [];
            })}
            onReorder={setSelectedIds} />
        </div>
      </div>
    </div>
  </Drawer>;
}
